/**
 * The half-typed player, kept while the app is away.
 *
 * Adding a guest to a round setup is one of the few places in this app where a
 * player has to LEAVE it mid-task: the name is in their head, the handicap is
 * in a WhatsApp message or on another club's website. On iOS the WebView can be
 * evicted while they are over there, and coming back to an empty wizard — never
 * mind an empty modal — means typing it all again.
 *
 * So the draft is written on every keystroke and read back when the modal
 * opens. `localStorage`, not memory or session: the point is to survive the
 * page being thrown away and rebuilt.
 *
 * Two things keep it from becoming a haunting:
 *
 *   - it is opt-in per caller (`draftKey`), so only the round-setup modal
 *     drafts. The society admin's player list is not a place anyone leaves the
 *     app mid-name, and a draft resurfacing there would be a surprise;
 *   - it expires. Two hours covers a phone call, a lookup, a delayed return; it
 *     does not cover next Saturday, when a stranger's half-typed name appearing
 *     in the box would be a bug rather than a kindness.
 */

const TTL_MS = 2 * 60 * 60 * 1000;

const storageKey = (key) => `player_draft_${key}`;

/** Everything the modal needs to come back exactly as it was left. */
export const readPlayerDraft = (key) => {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || typeof draft !== 'object') return null;
    if (!draft.at || Date.now() - draft.at > TTL_MS) {
      localStorage.removeItem(storageKey(key));
      return null;
    }
    // A draft of nothing is not a draft. Without this an opened-and-abandoned
    // modal would reopen itself on the next visit, forever.
    if (!draft.name && !draft.handicap) return null;
    return draft;
  } catch {
    return null;
  }
};

export const writePlayerDraft = (key, { name, handicap }) => {
  if (!key) return;
  try {
    if (!name && !handicap) {
      localStorage.removeItem(storageKey(key));
      return;
    }
    localStorage.setItem(storageKey(key), JSON.stringify({ name, handicap, at: Date.now() }));
  } catch {
    // A full or disabled store is not worth failing a keystroke over.
  }
};

export const clearPlayerDraft = (key) => {
  if (!key) return;
  try {
    localStorage.removeItem(storageKey(key));
  } catch {
    /* ignore */
  }
};
